import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { tokenStore } from '@/shared/auth/token-store';
import { useAuth } from '@/shared/auth';
import { API_BASE } from '@/shared/api/apiFetcher';

const bannerStyles = {
  position: 'sticky',
  top: 0,
  zIndex: 1300,
  bgcolor: 'error.dark',
  color: 'error.contrastText',
  px: 2,
  py: 1,
} as const;

const buttonStyles = {
  color: 'error.dark',
  bgcolor: 'common.white',
  '&:hover': { bgcolor: 'grey.100' },
} as const;

/**
 * Globally-visible non-dismissable red banner shown whenever the current
 * access token carries the `impersonated_by` claim. Exit calls
 * /api/auth/impersonation/end to write the matching audit row, clears the
 * token store, and tries to close the tab (admin-web opened it via
 * window.open). Fallback: navigate to /, which forces the user-app to
 * re-bootstrap from the HttpOnly cookie — which the impersonation tab
 * never had, so the next call 401s and the apiFetcher logs out.
 */
export const ImpersonationBanner = () => {
  // `tokenStore` is a module-level singleton with no event emitter. The
  // banner gets re-renders for free via `useAuth()` below — every token
  // mutation in P5 goes through the AuthProvider's lifecycle, so this
  // works by structural coincidence. If a future flow mutates the token
  // outside the auth context (debug toggles, event-bus rotation, etc.),
  // add a subscriber here.
  const claims = tokenStore.getImpersonationClaims();
  const { sessionUser } = useAuth();
  const [exiting, setExiting] = useState(false);

  if (!claims) {
    return null;
  }

  const targetName =
    sessionUser && (sessionUser.firstName || sessionUser.lastName)
      ? `${sessionUser.firstName ?? ''} ${sessionUser.lastName ?? ''}`.trim()
      : (sessionUser?.email ?? 'user');

  const expiresAt = new Date(claims.exp * 1000);

  const handleExit = async () => {
    if (exiting) {
      return;
    }
    setExiting(true);

    // Best-effort: write the impersonate_end audit row. We deliberately
    // don't block the exit on the network call — if apps/api is down the
    // admin still gets out of the impersonation session.
    try {
      await fetch(`${API_BASE}/auth/impersonation/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenStore.getAccessToken() ?? ''}`,
        },
      });
    } catch {
      // Network failed — the admin can still tell from admin-web's audit
      // log whether the end row was written.
    }

    tokenStore.clearTokens();
    // Close the tab if admin-web opened it via window.open (the usual
    // path). If it doesn't close (some browsers block when the tab wasn't
    // script-opened), fall through to navigation. The visibilityState
    // gate skips the navigation when close() succeeded — some browsers
    // run pending timers on the phantom page-context as the tab unloads.
    window.close();
    setTimeout(() => {
      if (document.visibilityState === 'visible') {
        window.location.href = '/';
      }
    }, 200);
  };

  return (
    <Box sx={bannerStyles} role="status" aria-live="polite">
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" component="div">
          <strong>Impersonating {targetName}</strong> — token expires {expiresAt.toLocaleTimeString()}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<LogoutIcon />}
          onClick={handleExit}
          disabled={exiting}
          sx={buttonStyles}
        >
          Exit impersonation
        </Button>
      </Stack>
    </Box>
  );
};
